import { useEffect, useRef, useState } from "react";
import {
  motion,
  useInView,
  AnimatePresence,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Zap,
  ChevronDown,
  Play,
  X,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ServicesHeader } from "@/components/ServicesHeader";
import { ServicesCatalog } from "@/components/ServicesCatalog";
import { ServicesWorkSection } from "@/components/ServicesWorkSection";
import { ServicesHeroThreads } from "@/components/ServicesHeroThreads";
import { BrandLogo } from "@/components/BrandLogo";
import { SEO, seoConfig } from "@/components/SEO";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { servicesPageCopy } from "@/lib/siteCopy";

type NavItem = {
  label: string;
  path?: string;
  href?: string;
  id?: string;
  type: "route" | "scroll" | "external";
};

// Hero Section
function HeroSection() {
  const navigate = useNavigate();
  const lang = useSiteLanguage();
  const t = servicesPageCopy[lang];
  const experienceRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [touchAnimationActive, setTouchAnimationActive] = useState(false);

  // Check for mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || "ontouchstart" in window);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Image URLs for cursor trail effect
  const trailImages = [
    "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400",
    "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400",
    "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=400",
    "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=400",
    "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400",
  ];

  // Optimized cursor trail animation with requestAnimationFrame pooling
  useEffect(() => {
    if (isMobile) return; // Skip cursor trail on mobile

    const experienceElement = experienceRef.current;
    const container = containerRef.current;
    if (!experienceElement || !container) return;

    let lastImageTime = 0;
    let lastX = 0;
    let lastY = 0;
    const minDistance = 60;
    const imageSpawnDelay = 120;
    let isMouseOver = false;
    let imageIndex = 0;
    const activeImages: HTMLImageElement[] = [];
    const maxActiveImages = 8; // Limit for performance

    // Preload images for smoother experience
    const preloadedImages: HTMLImageElement[] = [];
    trailImages.forEach((src) => {
      const img = new Image();
      img.src = src;
      preloadedImages.push(img);
    });

    const handleMouseMove = (e: MouseEvent) => {
      if (!isMouseOver) return;

      const containerRect = container.getBoundingClientRect();
      const currentMouseX = e.clientX - containerRect.left;
      const currentMouseY = e.clientY - containerRect.top;

      const currentTime = performance.now();
      const distance = Math.hypot(currentMouseX - lastX, currentMouseY - lastY);

      if (
        currentTime - lastImageTime >= imageSpawnDelay &&
        distance >= minDistance &&
        activeImages.length < maxActiveImages
      ) {
        spawnImage(currentMouseX, currentMouseY);
        lastImageTime = currentTime;
        lastX = currentMouseX;
        lastY = currentMouseY;
        imageIndex++;
      }
    };

    const spawnImage = (x: number, y: number) => {
      const img = document.createElement("img");
      const randomImage = trailImages[imageIndex % trailImages.length];
      img.src = randomImage;
      img.loading = "eager";

      const baseWidth = 180;
      const widthVariation = Math.random() * 50 - 25;
      const width = baseWidth + widthVariation;
      const height = width * 1.35;
      const rotation = Math.random() * 14 - 7;
      const offsetX = (Math.random() - 0.5) * 30;
      const offsetY = (Math.random() - 0.5) * 30;

      img.style.cssText = `
        position: absolute;
        left: ${x + offsetX}px;
        top: ${y + offsetY}px;
        width: ${width}px;
        height: ${height}px;
        object-fit: cover;
        pointer-events: none;
        will-change: transform, opacity;
        transform: translate(-50%, -50%) scale(0) rotate(${rotation}deg);
        opacity: 0;
        z-index: 9999;
        border-radius: 4px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1);
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
      `;

      container.appendChild(img);
      activeImages.push(img);

      // Use CSS transitions for GPU acceleration
      requestAnimationFrame(() => {
        img.style.transition =
          "transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.4s ease-out";
        img.style.opacity = "1";
        img.style.transform = `translate(-50%, -50%) scale(1) rotate(${rotation}deg)`;
      });

      // Fade out
      const fadeOutTimeout = setTimeout(() => {
        img.style.transition =
          "transform 0.7s cubic-bezier(0.4, 0, 0.6, 1), opacity 0.5s ease-in";
        img.style.opacity = "0";
        const exitRotation = rotation + (Math.random() * 8 - 4);
        img.style.transform = `translate(-50%, -50%) scale(0.85) rotate(${exitRotation}deg)`;
      }, 1000);

      // Remove element
      const removeTimeout = setTimeout(() => {
        if (img.parentNode) {
          img.remove();
          const idx = activeImages.indexOf(img);
          if (idx > -1) activeImages.splice(idx, 1);
        }
      }, 1700);

      (img as any)._timeouts = [fadeOutTimeout, removeTimeout];
    };

    const handleMouseEnter = () => {
      isMouseOver = true;
      imageIndex = 0;
    };

    const handleMouseLeave = () => {
      isMouseOver = false;
      // Graceful cleanup
      activeImages.forEach((img) => {
        if (img.parentNode) {
          img.style.transition = "opacity 0.3s ease-out";
          img.style.opacity = "0";
          setTimeout(() => img.remove(), 300);
        }
      });
      activeImages.length = 0;
    };

    experienceElement.addEventListener("mouseenter", handleMouseEnter);
    experienceElement.addEventListener("mouseleave", handleMouseLeave);
    experienceElement.addEventListener("mousemove", handleMouseMove);

    return () => {
      experienceElement.removeEventListener("mouseenter", handleMouseEnter);
      experienceElement.removeEventListener("mouseleave", handleMouseLeave);
      experienceElement.removeEventListener("mousemove", handleMouseMove);

      activeImages.forEach((img) => {
        const timeouts = (img as any)._timeouts;
        if (timeouts) timeouts.forEach(clearTimeout);
        img.remove();
      });
    };
  }, [trailImages, isMobile]);

  // Mobile touch animation
  const handleMobileTap = () => {
    if (!isMobile) return;
    setTouchAnimationActive(true);
    setTimeout(() => setTouchAnimationActive(false), 1200);
  };

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen md:h-screen flex flex-col justify-center overflow-x-hidden overflow-y-visible px-4 sm:px-6 pt-24"
    >
      <ServicesHeroThreads />

      {/* Geometric Elements - hidden on mobile for performance */}
      <div className="absolute inset-0 z-[2] overflow-hidden hidden md:block">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 border border-border/20 rotate-45 transform -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 border border-border/20 rotate-12 transform translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Main Hero Text */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-center"
          >
            <h1 className="hero-title text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light leading-[1.08] tracking-tight">
              <span className="block text-foreground">
                {t.heroLine1}
              </span>
              <span className="block text-foreground mt-1 sm:mt-2">
                {t.heroLine2}
              </span>
              <span className="block text-foreground mt-1 sm:mt-2">
                {t.heroLine3}{" "}
                <span
                  ref={experienceRef}
                  onClick={handleMobileTap}
                  className="relative inline-block cursor-pointer select-none z-50 px-1 sm:px-2 overflow-visible"
                >
                  {/* Gradient text with smooth hover effect */}
                  <motion.span
                    className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 bg-clip-text text-transparent font-medium italic"
                    animate={
                      touchAnimationActive
                        ? {
                            scale: [1, 1.05, 1],
                          }
                        : {}
                    }
                    transition={{ duration: 0.4 }}
                  >
                    {t.heroExperience}
                  </motion.span>

                  {/* Mobile tap hint - pulsing effect */}
                  {isMobile && (
                    <motion.span
                      className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-slate-400 whitespace-nowrap"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      {t.tapHint}
                    </motion.span>
                  )}

                  {/* Mobile touch animation - image stack on tap */}
                  <AnimatePresence>
                    {touchAnimationActive && isMobile && (
                      <div className="absolute left-1/2 top-0 -translate-x-1/2 w-[min(100vw,20rem)] h-52 pointer-events-none z-[60]">
                        {[0, 1, 2].map((i) => (
                          <motion.div
                            key={i}
                            className="absolute top-6 w-32 h-40"
                            style={{
                              left: `calc(50% + ${(i - 1) * 76}px)`,
                              translate: "-50% 0",
                            }}
                            initial={{
                              opacity: 0,
                              scale: 0.45,
                              y: 0,
                              rotate: -12 + i * 12,
                            }}
                            animate={{
                              opacity: [0, 1, 0.9, 0],
                              scale: [0.45, 1, 0.96, 0.88],
                              y: [-10, -95 - i * 22, -115 - i * 22],
                              rotate: -14 + i * 14,
                            }}
                            exit={{ opacity: 0, scale: 0.5 }}
                            transition={{
                              duration: 1.1,
                              delay: i * 0.08,
                              ease: [0.23, 1, 0.32, 1],
                            }}
                          >
                            <div className="w-full h-full rounded-xl overflow-hidden shadow-2xl ring-2 ring-white/25">
                              <img
                                src={trailImages[i]}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </AnimatePresence>

                  {/* Subtle underline indicator - desktop only */}
                  <span className="hidden md:block absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-500/50 via-orange-500/50 to-red-500/50 transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100" />
                </span>
              </span>
            </h1>
          </motion.div>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-center mt-6 sm:mt-8 text-base sm:text-lg text-muted-foreground font-light tracking-wide max-w-lg mx-auto px-4"
          >
            {t.heroSub}
          </motion.p>

          {/* Minimal CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="flex justify-center mt-12 gap-6"
          >
            <Button
              onClick={() =>
                navigate("/contact", {
                  state: {
                    query: t.startProjectQuery,
                    service: "Services",
                    message: t.startProjectMessage,
                  },
                })
              }
              variant="outline"
              size="lg"
              className="rounded-full px-8 py-6 text-base font-normal border-border hover:bg-muted/60 transition-all duration-300 group"
            >
              {t.startProject}
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <ChevronDown className="w-7 h-6 text-slate-400" />
      </motion.div>
    </section>
  );
}

// Strong CTA Section
function StrongCTASection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const navigate = useNavigate();
  const lang = useSiteLanguage();
  const t = servicesPageCopy[lang];
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [count, setCount] = useState(0);
  const targetCount = 150;

  useEffect(() => {
    if (isInView && count < targetCount) {
      const timer = setTimeout(() => {
        setCount((prev) => Math.min(prev + 3, targetCount));
      }, 20);
      return () => clearTimeout(timer);
    }
  }, [isInView, count]);

  const floatY = useMotionValue(0);
  const springY = useSpring(floatY, { stiffness: 100, damping: 20 });

  useEffect(() => {
    let frame: number;
    let t = 0;
    const animate = () => {
      t += 0.02;
      floatY.set(Math.sin(t) * 8);
      frame = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(frame);
  }, [floatY]);

  return (
    <section ref={ref} className="py-24 md:py-40 relative overflow-hidden">
      {/* Dynamic background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-primary/10" />
        <motion.div
          className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(168, 85, 247, 0.08) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -40, 0],
            y: [0, 40, 0],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <Badge
              variant="outline"
              className="mb-6 px-4 py-1.5 border-primary/30 text-primary"
            >
              <Sparkles className="w-3 h-3 mr-2" />
              {t.ctaBadge}
            </Badge>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-[1.1]">
              {t.ctaTitleBefore}
              <br />
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-primary via-blue-500 to-primary bg-clip-text text-transparent">
                  {t.ctaTitleAccent}
                </span>
                <motion.span
                  className="absolute -bottom-2 left-0 w-full h-1.5 bg-gradient-to-r from-primary/60 via-blue-500 to-primary/60 rounded-full"
                  initial={{ scaleX: 0 }}
                  animate={isInView ? { scaleX: 1 } : {}}
                  transition={{ duration: 0.8, delay: 0.5 }}
                />
              </span>
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed max-w-lg">
              {t.ctaSubtitle.replace("{count}", String(count))}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  size="lg"
                  className="w-full sm:w-auto text-base px-8 py-6 group bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25"
                  onClick={() =>
                    navigate("/contact", {
                      state: {
                        query: t.freeDemoQuery,
                        service: "Services Demo",
                        message: t.freeDemoMessage,
                      },
                    })
                  }
                >
                  <Zap className="mr-2 h-5 w-5" />
                  {t.ctaPrimary}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto text-base px-8 py-6 border-2 hover:bg-muted/50"
                  onClick={() => setShowVideoModal(true)}
                >
                  <Play className="mr-2 h-5 w-5" />
                  {t.watchDemo}
                </Button>
              </motion.div>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span>{t.freeConsultation}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span>{t.noCommitment}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span>{t.fastResponse}</span>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Video Preview Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{ y: springY }}
            className="relative"
          >
            <motion.div
              className="relative rounded-3xl overflow-hidden border border-border/50 bg-card/50 backdrop-blur-xl shadow-2xl cursor-pointer group"
              whileHover={{ scale: 1.02 }}
              onClick={() => setShowVideoModal(true)}
            >
              <div className="aspect-video bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.1)_0%,transparent_70%)]" />

                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <motion.div
                    className="mb-4 opacity-20"
                    animate={{ opacity: [0.15, 0.35, 0.15] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <BrandLogo showText={false} size="xl" variant="dark" />
                  </motion.div>

                  <motion.div
                    className="w-20 h-20 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/30"
                    whileHover={{ scale: 1.1 }}
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Play className="w-8 h-8 text-white ml-1" fill="white" />
                  </motion.div>

                  <p className="text-white/60 mt-4 text-sm">
                    {t.watch2Min}
                  </p>
                </div>
              </div>

              <div className="p-4 bg-card/80 backdrop-blur-sm border-t border-border/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{t.platformOverview}</p>
                    <p className="text-xs text-muted-foreground">
                      {t.videoMeta}
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    <Play className="w-3 h-3 mr-1" />
                    {t.demoBadge}
                  </Badge>
                </div>
              </div>
            </motion.div>

            {/* Decorative elements */}
            <motion.div
              className="absolute -top-6 -right-6 w-24 h-24 rounded-2xl bg-primary/10 border border-primary/20 backdrop-blur-sm -z-10"
              animate={{ rotate: [0, 5, 0] }}
              transition={{ duration: 6, repeat: Infinity }}
            />
            <motion.div
              className="absolute -bottom-4 -left-4 w-16 h-16 rounded-xl bg-blue-500/10 border border-blue-500/20 backdrop-blur-sm -z-10"
              animate={{ rotate: [0, -5, 0] }}
              transition={{ duration: 8, repeat: Infinity }}
            />
          </motion.div>
        </div>
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowVideoModal(false)}
          >
            <motion.div
              className="relative w-full max-w-4xl aspect-video bg-slate-900 rounded-2xl overflow-hidden border border-border/30"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowVideoModal(false)}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="mb-6 opacity-10">
                  <BrandLogo showText={false} size="xl" variant="dark" />
                </div>
                <p className="text-white/60 text-lg">{t.demoComingSoon}</p>
                <p className="text-white/40 text-sm mt-2">
                  {t.contactForLive}
                </p>

                <Button
                  className="mt-6"
                  onClick={() => {
                    setShowVideoModal(false);
                    navigate("/contact", {
                      state: {
                        query: t.liveDemoQuery,
                        service: "Services Demo",
                        message: t.liveDemoMessage,
                      },
                    });
                  }}
                >
                  {t.requestLiveDemo}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// Main Services Page
interface ServicesPageProps {
  navItems: NavItem[];
  onMobileMenuOpen?: () => void;
}

export function ServicesPage({
  navItems,
  onMobileMenuOpen,
}: ServicesPageProps) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <>
      <SEO {...seoConfig.services} />
      <div className="min-h-screen bg-background">
        <ServicesHeader
          navItems={navItems}
          onMobileMenuOpen={onMobileMenuOpen}
        />
        <HeroSection />
        <ServicesCatalog />
        <ServicesWorkSection />
        <StrongCTASection />
      </div>
    </>
  );
}

export default ServicesPage;
